import React, { useState, useEffect, forwardRef, useImperativeHandle, useMemo } from "react";
import {
  Card,
  CardBody,
  Label,
  Input,
  FormGroup,
} from "reactstrap";
import classNames from "classnames";
import Select from "components/Select";
import ReactDatetime from "components/CustomDatePicker";
import { useUpdateInventory } from "../../../api/inventory/service/index";
import { useNavigate } from "react-router-dom";
import { formatPriceWithDollar, toNumber } from "../../../utils/currency";
import { formatDateForAPI, formatDateForPicker } from "../../../utils/dateUtils";
import { getActiveColorStyle } from "utils/colorUtils";
import {
  getTheme2FormFieldsCss,
  getTheme2SearchFilterSingleSelectStyles,
} from "utils/theme2MultiSelect";
import { THEME2_SIDEBAR_NAV_FONT_SIZE_VAR } from "utils/sidebarShellTheme";
import { useTranslation } from 'react-i18next';
import { useGetChannels } from "api/channels/service";
import ChannelsEmbedded from "views/settings/Channels/ChannelsEmbedded";

const MARKETING_PROVIDERS = [
  { key: "cargurus", labelKey: "pricingTab.marketingProviders.cargurus" },
  {
    key: "dealertiq_default_service",
    labelKey: "pricingTab.marketingProviders.dealertiqDefaultService",
  },
];

const toBool = (value) =>
  value === true || value === "true" || value === 1 || value === "1";

const PricingTab = forwardRef(
  ({ inventoryId, inventoryData, isLoading, activeColor = "secondary", isTheme2 = false }, ref) => {
  const { t } = useTranslation();
  const updateMutation = useUpdateInventory();
  const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);
  const filterSelectStyles = useMemo(
    () => getTheme2SearchFilterSingleSelectStyles(activeColorStyle, isTheme2),
    [activeColorStyle, isTheme2]
  );
  const theme2FieldsCss = useMemo(
    () =>
      isTheme2
        ? `${getTheme2FormFieldsCss({
            scopeSelector: ".pricing-tab.pricing-tab--theme2",
            fieldsSelector: ".pricing-tab-fields",
          })}
          .pricing-tab.pricing-tab--theme2 .internet-marketing-uploads .form-check {
            margin-top: 0.35rem;
            margin-bottom: 0.35rem;
          }
          .pricing-tab.pricing-tab--theme2 .internet-marketing-uploads .form-check-label {
            font-size: ${THEME2_SIDEBAR_NAV_FONT_SIZE_VAR} !important;
            display: inline-flex;
            align-items: center;
          }`
        : "",
    [isTheme2]
  );
  const inputClassName = isTheme2 ? "form-control" : undefined;
  const inputStyle = isTheme2
    ? { fontSize: THEME2_SIDEBAR_NAV_FONT_SIZE_VAR }
    : { fontSize: "14px" };
  const readOnlyValueStyle = isTheme2
    ? { fontSize: THEME2_SIDEBAR_NAV_FONT_SIZE_VAR }
    : { fontSize: "14px" };
  const getDateInputProps = (extra = {}) => ({
    className: "form-control",
    style: isTheme2
      ? { fontSize: THEME2_SIDEBAR_NAV_FONT_SIZE_VAR }
      : undefined,
    ...extra,
  });

  const [formData, setFormData] = useState({
    normal_asking_price: '',
    promotional_price: '',
    wholesale_price: '',
    asking_down: '',
    asking_term_months: '',
    guide_type: '',
    guide_details: '',
    primary_lot: '',
    acquired_date: '',
    vehicle_cost: '',
    pack_fee: '',
    total_cost: '',
    repair_cost: '',
    total_cost_in_car: '',
    floor_plan_expense: '',
    vehicle_expenses: '',
    max_terms: '',
    repair_orders_count: '',
    // Trade-in specific
    mileage: '',
    mileage_status: '',
    evaluation_date: '',
    evaluation_group: '',
    auction_lane: '',
    auction_number: '',
    offer_price: '',
    bid_price: '',
    bid_expires: '',
    marketing: {
      cargurus: false,
      dealertiq_default_service: false,
    },
  });

  const navigate = useNavigate();
  const { data: locationsData } = useGetChannels("sales_location_branch");
  const [showAddLot, setShowAddLot] = useState(false);

  // API'den gelen verileri formData'ya set et
  useEffect(() => {
    if (inventoryData) {
      const marketingContent = inventoryData?.inventory_marketing?.content || {};
      setFormData({
        // base_pricing_json
        normal_asking_price: formatPriceWithDollar(inventoryData?.inventory_pricing?.normal_asking_price) || 0,
        promotional_price: formatPriceWithDollar(inventoryData?.inventory_pricing?.promotional_price) || 0,
        wholesale_price: formatPriceWithDollar(inventoryData?.inventory_pricing?.wholesale_price) || 0,
        asking_down: formatPriceWithDollar(inventoryData?.inventory_pricing?.asking_down) || 0,
        asking_term_months: inventoryData?.inventory_pricing?.asking_term_months || 0,
        // buyers_guide_json
        guide_type: inventoryData?.inventory_pricing?.buyers_guide_json?.guide_type || '',
        guide_details: inventoryData?.inventory_pricing?.buyers_guide_json?.details || '',
        // cost_details_json
        primary_lot: inventoryData?.location?.id ? String(inventoryData.location.id) : '',
        acquired_date: formatDateForPicker(inventoryData?.acquired_date),
        vehicle_cost: formatPriceWithDollar(inventoryData?.inventory_pricing?.vehicle_cost) || '',
        pack_fee: formatPriceWithDollar(inventoryData?.inventory_pricing?.pack_fee) || '',
        total_cost: formatPriceWithDollar(inventoryData?.total_cost) || '',
        repair_cost: formatPriceWithDollar(inventoryData?.repair_cost) || '',
        total_cost_in_car: formatPriceWithDollar(inventoryData?.inventory_pricing?.total_cost_in_car) || '',
        floor_plan_expense: formatPriceWithDollar(inventoryData?.inventory_pricing?.floor_plan_expense) || '',
        vehicle_expenses: formatPriceWithDollar(inventoryData?.vehicle_expenses) || '',
        max_terms: inventoryData?.max_terms || '',
        repair_orders_count: inventoryData?.repair_orders_count || '',
        // trade-in fields
        mileage: inventoryData?.inventory_vehicle?.actual_mileage || '',
        mileage_status: inventoryData?.inventory_vehicle?.actual_mileage_status || '',
        evaluation_date: formatDateForPicker(inventoryData?.inventory_pricing?.evaluation_date),
        evaluation_group: inventoryData?.inventory_pricing?.evaluation_group || '',
        auction_lane: inventoryData?.inventory_pricing?.auction_lane || '',
        auction_number: inventoryData?.inventory_pricing?.auction_number || '',
        offer_price: formatPriceWithDollar(inventoryData?.inventory_pricing?.offer_price) || 0,
        bid_price: formatPriceWithDollar(inventoryData?.inventory_pricing?.bid_price) || 0,
        bid_expires: formatDateForPicker(inventoryData?.inventory_pricing?.bid_expires),
        marketing: {
          cargurus: toBool(marketingContent.cargurus),
          dealertiq_default_service: toBool(marketingContent.dealertiq_default_service),
        },
      });
    }
  }, [inventoryData]);

  const handleInputChange = (field, value) => {
    // Sayısal alanlar için temizleme yap
    const numericFields = [
      'normal_asking_price', 'promotional_price', 'wholesale_price', 'asking_down',
      'vehicle_cost', 'pack_fee', 'total_cost', 'repair_cost', 'total_cost_in_car',
      'floor_plan_expense', 'vehicle_expenses', 'offer_price', 'bid_price'
    ];

    if (numericFields.includes(field)) {
      // Sadece sayı ve nokta karakterlerini kabul et
      const cleanedValue = value.replace(/[^\d.]/g, '');
      setFormData(prev => ({
        ...prev,
        [field]: cleanedValue
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleMarketingToggle = (key) => {
    setFormData((prev) => ({
      ...prev,
      marketing: {
        ...prev.marketing,
        [key]: !prev.marketing?.[key],
      },
    }));
  };

  const handleLocationChange = (value) => {
    if (value === "add_new_lot") {
      setShowAddLot(true);
    } else {
      handleInputChange('primary_lot', String(value));
    }
  };

  // Güncelleme fonksiyonu
  const handleSave = async () => {
    const marketingContent = {
      cargurus: !!formData.marketing?.cargurus,
      dealertiq_default_service: !!formData.marketing?.dealertiq_default_service,
    };

    const existingMarketing = inventoryData?.inventory_marketing;
    const inventoryMarketingPayload = {
      content: marketingContent,
    };
    if (existingMarketing?.documentId) {
      inventoryMarketingPayload.documentId = existingMarketing.documentId;
    } else if (existingMarketing?.document_id) {
      inventoryMarketingPayload.documentId = existingMarketing.document_id;
    }
    if (existingMarketing?.id != null) {
      inventoryMarketingPayload.id = existingMarketing.id;
    }

    const updateData = {
      location: {
        id: formData.primary_lot || null,
      },
      acquired_date: formatDateForAPI(formData.acquired_date),
      pricing: {
        normal_asking_price: toNumber(formData.normal_asking_price),
        promotional_price: toNumber(formData.promotional_price),
        wholesale_price: toNumber(formData.wholesale_price),
        asking_down: toNumber(formData.asking_down),
        asking_term_months: toNumber(formData.asking_term_months),
        buyers_guide_json: {
          guide_type: formData.guide_type || '',
          details: formData.guide_details || '',
        },
        vehicle_cost: toNumber(formData.vehicle_cost),
        pack_fee: toNumber(formData.pack_fee),
        total_cost_in_car: toNumber(formData.total_cost_in_car),
        // Evaluation fields inside pricing
        evaluation_date: formatDateForAPI(formData.evaluation_date),
        evaluation_group: formData.evaluation_group,
        auction_lane: formData.auction_lane,
        auction_number: formData.auction_number,
        offer_price: toNumber(formData.offer_price),
        bid_price: toNumber(formData.bid_price),
        bid_expires: formatDateForAPI(formData.bid_expires),
      },
      vehicle: {
        actual_mileage: formData.mileage,
        actual_mileage_status: formData.mileage_status,
      },
      inventory_marketing: inventoryMarketingPayload,
    };


    if (!inventoryId) {
      console.error('No valid ID found. Available IDs:', {
        inventoryDataId: inventoryData?.id,
        inventoryId: inventoryId
      });
      return { success: false, error: 'No valid ID found' };
    }


    // InventoryOptions.tsx'deki gibi direkt mutate çağrısı
    updateMutation.mutate({ id: inventoryId, data: updateData });

    return { success: true };
  };

  // Parent component'e fonksiyonları expose et
  useImperativeHandle(
    ref,
    () => ({
      handleSave,
    }),
    [formData, inventoryId, inventoryData, updateMutation]
  );

  if (isLoading) {
    return (
      <div className="content">
        <Card>
          <CardBody className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="sr-only">Loading...</span>
            </div>
            <p className='mt-3'>{t('pricingTab.loadingPricingInfo')}</p>
          </CardBody>
        </Card>
      </div>
    );
  }

  return (
    <div className={classNames("pricing-tab", { "pricing-tab--theme2": isTheme2 })}>
      {isTheme2 ? <style>{theme2FieldsCss}</style> : null}
      {showAddLot && <ChannelsEmbedded
        activeColor={activeColor}
        defaultType="sales_location_branch"
        onClose={() => setShowAddLot(false)}
        onCreated={(created) => {
          if (created?.id) {
            handleInputChange("primary_lot", String(created.id));
          } else if (created?.name) {
            handleInputChange("primary_lot", String(created.name));
          }
        }}
      />}
      {!showAddLot &&
        <div className={classNames("row", { "pricing-tab-fields": isTheme2 })}>
          {inventoryData?.type === "trade_in" ? (
            <div className="col-md-6 mb-4">
              <div className="card">
                <div className="card-header font-weight-bold text-uppercase">EVALUATION</div>
                <div className="card-body">
                  <div className="form-group">
                    <Label>Vehicle Lot:</Label>
                    <Select
                      activeColor={activeColor}
                      className="react-select"
                      styles={filterSelectStyles}
                      placeholder={t('pricingTab.selectLot')}
                      name="primary_lot"
                      isSearchable
                      closeMenuOnSelect={true}
                      value={formData.primary_lot ? {
                        value: formData.primary_lot,
                        label: formData.primary_lot === "add_new_lot"
                          ? t('pricingTab.addNewLot')
                          : locationsData?.results?.find(loc => String(loc.id) === String(formData.primary_lot))?.name || formData.primary_lot
                      } : null}
                      onChange={(option) => {
                        if (option?.value === "add_new_lot") {
                          handleLocationChange("add_new_lot");
                        } else {
                          handleLocationChange(option?.value || "");
                        }
                      }}
                      options={[
                        { value: "add_new_lot", label: t('pricingTab.addNewLot') },
                        ...(locationsData?.results?.map((location) => ({
                          value: String(location.id),
                          label: location.name
                        })) || [])
                      ]}
                      isClearable={false}
                    />
                  </div>
                  <div className="form-group">
                    <Label>Actual Mileage:</Label>
                    <Input
                      className={inputClassName}
                      style={inputStyle}
                      type="text"
                      value={formData.mileage}
                      onChange={(e) => handleInputChange('mileage', e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <Label>Actual Mileage Status:</Label>
                    <Select
                      activeColor={activeColor}
                      className="react-select"
                      styles={filterSelectStyles}
                      placeholder="Select Status"
                      name="mileage_status"
                      value={formData.mileage_status ? { value: formData.mileage_status, label: formData.mileage_status } : null}
                      onChange={(option) => handleInputChange('mileage_status', option?.value || "")}
                      options={[
                        { value: "Actual Miles", label: "Actual Miles" },
                        { value: "True Mileage Unknown", label: "True Mileage Unknown" },
                        { value: "Beyond Mechnical Limits", label: "Beyond Mechnical Limits" },
                        { value: "Exempt", label: "Exempt" },
                      ]}
                    />
                  </div>
                  <div className="form-group">
                    <Label>Evaluation Date:</Label>
                    <ReactDatetime
                      inputProps={getDateInputProps()}
                      value={formData.evaluation_date}
                      onChange={(date) => handleInputChange('evaluation_date', date)}
                      dateFormat="MM-DD-YYYY"
                      timeFormat={false}
                      closeOnSelect={true}
                    />
                  </div>
                  <div className="form-group">
                    <Label>Evaluation Group:</Label>
                    <Select
                      activeColor={activeColor}
                      className="react-select"
                      styles={filterSelectStyles}
                      placeholder="Select Group"
                      name="evaluation_group"
                      value={formData.evaluation_group ? { value: formData.evaluation_group, label: formData.evaluation_group } : null}
                      onChange={(option) => handleInputChange('evaluation_group', option?.value || "")}
                      options={[]}
                    />
                  </div>
                  <div className="form-group">
                    <Label>Auction Lane:</Label>
                    <Input
                      className={inputClassName}
                      style={inputStyle}
                      type="text"
                      value={formData.auction_lane}
                      onChange={(e) => handleInputChange('auction_lane', e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <Label>Auction Number:</Label>
                    <Input
                      className={inputClassName}
                      style={inputStyle}
                      type="text"
                      value={formData.auction_number}
                      onChange={(e) => handleInputChange('auction_number', e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <Label>Offer Price: <i className="fa fa-info-circle text-info" /></Label>
                    <Input
                      className={inputClassName}
                      style={inputStyle}
                      type="text"
                      value={formData.offer_price}
                      onChange={(e) => handleInputChange('offer_price', e.target.value)}
                      onBlur={() => setFormData(prev => ({ ...prev, offer_price: formatPriceWithDollar(formData.offer_price) }))}
                      onFocus={() => {
                        const raw = formData.offer_price.toString().replace(/[^\d.]/g, '');
                        setFormData(prev => ({ ...prev, offer_price: raw }));
                      }}
                    />
                  </div>
                  <div className="form-group">
                    <Label>Bid Price: <i className="fa fa-info-circle text-info" /></Label>
                    <Input
                      className={inputClassName}
                      style={inputStyle}
                      type="text"
                      value={formData.bid_price}
                      onChange={(e) => handleInputChange('bid_price', e.target.value)}
                      onBlur={() => setFormData(prev => ({ ...prev, bid_price: formatPriceWithDollar(formData.bid_price) }))}
                      onFocus={() => {
                        const raw = formData.bid_price.toString().replace(/[^\d.]/g, '');
                        setFormData(prev => ({ ...prev, bid_price: raw }));
                      }}
                    />
                  </div>
                  <div className="form-group">
                    <Label>Bid Expires on: <i className="fa fa-info-circle text-info" /></Label>
                    <ReactDatetime
                      inputProps={getDateInputProps()}
                      value={formData.bid_expires}
                      onChange={(date) => handleInputChange('bid_expires', date)}
                      dateFormat="MM-DD-YYYY"
                      timeFormat={false}
                      closeOnSelect={true}
                    />
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Fiyatlar Card */}
              <div className="col-md-4 mb-4">
                <div className="card">
                  <div className="card-header font-weight-bold">{t('pricingTab.pricing')}</div>
                  <div className="card-body">
                    <div className="form-group">
                      <Label>{t('pricingTab.normalAskingPrice')}</Label>
                      <Input
                        className={inputClassName}
                        style={inputStyle}
                        type="text"
                        value={formData.normal_asking_price}
                        onChange={(e) => handleInputChange('normal_asking_price', e.target.value)}
                        onBlur={(e) => {
                          setFormData(prev => ({
                            ...prev,
                            normal_asking_price: formatPriceWithDollar(formData.normal_asking_price)
                          }));
                        }}
                        onFocus={(e) => {
                          const raw = formData.normal_asking_price.toString().replace(/[^\d.]/g, '');
                          setFormData(prev => ({ ...prev, normal_asking_price: raw }));
                        }}
                        placeholder="0.00"
                      />
                    </div>
                    <div className="form-group">
                      <Label>{t('pricingTab.promotionalPrice')}</Label>
                      <Input
                        className={inputClassName}
                        style={inputStyle}
                        type="text"
                        value={formData.promotional_price}
                        onChange={(e) => handleInputChange('promotional_price', e.target.value)}
                        onBlur={(e) => {
                          setFormData(prev => ({
                            ...prev,
                            promotional_price: formatPriceWithDollar(formData.promotional_price)
                          }));
                        }}
                        onFocus={(e) => {
                          const raw = formData.promotional_price.toString().replace(/[^\d.]/g, '');
                          setFormData(prev => ({ ...prev, promotional_price: raw }));
                        }}
                        placeholder="0.00"
                      />
                    </div>
                    <div className="form-group">
                      <Label>{t('pricingTab.wholesalePrice')}</Label>
                      <Input
                        className={inputClassName}
                        style={inputStyle}
                        type="text"
                        value={formData.wholesale_price}
                        onChange={(e) => handleInputChange('wholesale_price', e.target.value)}
                        onBlur={(e) => {
                          setFormData(prev => ({
                            ...prev,
                            wholesale_price: formatPriceWithDollar(formData.wholesale_price)
                          }));
                        }}
                        onFocus={(e) => {
                          const raw = formData.wholesale_price.toString().replace(/[^\d.]/g, '');
                          setFormData(prev => ({ ...prev, wholesale_price: raw }));
                        }}
                        placeholder="0.00"
                      />
                    </div>
                    <div className="form-group">
                      <Label>{t('pricingTab.askingDown')}</Label>
                      <Input
                        className={inputClassName}
                        style={inputStyle}
                        type="text"
                        value={formData.asking_down}
                        onChange={(e) => handleInputChange('asking_down', e.target.value)}
                        onBlur={(e) => {
                          setFormData(prev => ({
                            ...prev,
                            asking_down: formatPriceWithDollar(formData.asking_down)
                          }));
                        }}
                        onFocus={(e) => {
                          const raw = formData.asking_down.toString().replace(/[^\d.]/g, '');
                          setFormData(prev => ({ ...prev, asking_down: raw }));
                        }}
                        placeholder="0.00"
                      />
                    </div>
                    <div className="form-group">
                      <Label>{t('pricingTab.askingTermMonths')}</Label>
                      <Input
                        className={inputClassName}
                        style={inputStyle}
                        type="number"
                        value={formData.asking_term_months}
                        onChange={(e) => handleInputChange('asking_term_months', e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                {/* Buyers Guide under Pricing */}
                <div className="card mt-2">
                  <div className="card-header font-weight-bold text-uppercase">{t('pricingTab.buyersGuide')}</div>
                  <div className="card-body">
                    <div className="form-group">
                      <Label>{t('pricingTab.buyersGuideLabel')}</Label>
                      <Input
                        className={inputClassName}
                        style={inputStyle}
                        type="text"
                        value={formData.guide_type}
                        onChange={(e) => handleInputChange('guide_type', e.target.value)}
                        disabled
                      />
                    </div>
                    <div className="form-group">
                      <Label>{t('pricingTab.maxTerms')}</Label>
                      <Input
                        className={inputClassName}
                        style={inputStyle}
                        type="number"
                        value={formData.max_terms}
                        onChange={(e) => handleInputChange('max_terms', e.target.value)}
                        disabled
                      />
                    </div>
                    <div className="form-group">
                      <Label>{t('pricingTab.repairOrders')}</Label>
                      <Input
                        className={inputClassName}
                        style={inputStyle}
                        type="number"
                        value={formData.repair_orders_count}
                        onChange={(e) => handleInputChange('repair_orders_count', e.target.value)}
                        disabled
                      />
                    </div>
                    <div className="form-group">
                      <Label>{t('pricingTab.repairCost')}</Label>
                      <Input
                        className={inputClassName}
                        style={inputStyle}
                        type="text"
                        value={formData.repair_cost}
                        onChange={(e) => handleInputChange('repair_cost', e.target.value)}
                        onBlur={(e) => {
                          setFormData(prev => ({
                            ...prev,
                            repair_cost: formatPriceWithDollar(formData.repair_cost)
                          }));
                        }}
                        onFocus={(e) => {
                          const raw = formData.repair_cost.toString().replace(/[^\d.]/g, '');
                          setFormData(prev => ({ ...prev, repair_cost: raw }));
                        }}
                        placeholder="0.00"
                        disabled
                      />
                    </div>
                    <div className="form-group">
                      <Label>{t('pricingTab.totalCost')}</Label>
                      <Input
                        className={inputClassName}
                        style={inputStyle}
                        type="text"
                        value={formData.total_cost}
                        onChange={(e) => handleInputChange('total_cost', e.target.value)}
                        onBlur={(e) => {
                          setFormData(prev => ({
                            ...prev,
                            total_cost: formatPriceWithDollar(formData.total_cost)
                          }));
                        }}
                        onFocus={(e) => {
                          const raw = formData.total_cost.toString().replace(/[^\d.]/g, '');
                          setFormData(prev => ({ ...prev, total_cost: raw }));
                        }}
                        placeholder="0.00"
                        disabled
                      />
                    </div>
                  </div>
                </div>
              </div>
              {/* Cost Details Card */}
              <div className="col-md-4 mb-4">
                <div className="card">
                  <div className="card-header font-weight-bold text-uppercase">{t('pricingTab.costDetails')}</div>
                  <div className="card-body">
                    <div className="form-group">
                      <Label>{t('pricingTab.primaryLot')}</Label>
                      <Select
                        activeColor={activeColor}
                        className="react-select"
                        styles={filterSelectStyles}
                        placeholder={t('pricingTab.selectLot')}
                        name="primary_lot"
                        isSearchable
                        closeMenuOnSelect={true}
                        value={formData.primary_lot ? {
                          value: formData.primary_lot,
                          label: formData.primary_lot === "add_new_lot"
                            ? t('pricingTab.addNewLot')
                            : locationsData?.results?.find(loc => String(loc.id) === String(formData.primary_lot))?.name || formData.primary_lot
                        } : null}
                        onChange={(option) => {
                          if (option?.value === "add_new_lot") {
                            handleLocationChange("add_new_lot");
                          } else {
                            handleLocationChange(option?.value || "");
                          }
                        }}
                        options={[
                          { value: "add_new_lot", label: t('pricingTab.addNewLot') },
                          ...(locationsData?.results?.map((location) => ({
                            value: String(location.id),
                            label: location.name
                          })) || [])
                        ]}
                        isClearable={false}
                      />
                    </div>
                    <div className="form-group">
                      <Label>{t('pricingTab.purchaseDate')}</Label>
                      <ReactDatetime
                        inputProps={getDateInputProps({
                          placeholder: t('pricingTab.selectDate'),
                        })}
                        value={formData.acquired_date}
                        onChange={(date) => handleInputChange('acquired_date', date)}
                        dateFormat="MM-DD-YYYY"
                        timeFormat={false}
                        closeOnSelect={true}
                      />
                    </div>
                    <div className="form-group">
                      <Label>{t('pricingTab.vehicleCost')}</Label>
                      <Input
                        className={inputClassName}
                        style={inputStyle}
                        type="text"
                        value={formData.vehicle_cost}
                        onChange={(e) => handleInputChange('vehicle_cost', e.target.value)}
                        onBlur={(e) => {
                          setFormData(prev => ({
                            ...prev,
                            vehicle_cost: formatPriceWithDollar(formData.vehicle_cost)
                          }));
                        }}
                        onFocus={(e) => {
                          const raw = formData.vehicle_cost.toString().replace(/[^\d.]/g, '');
                          setFormData(prev => ({ ...prev, vehicle_cost: raw }));
                        }}
                        placeholder="0.00"
                      />
                    </div>
                    <div className="form-group">
                      <Label>{t('pricingTab.packFee')}</Label>
                      <Input
                        className={inputClassName}
                        style={inputStyle}
                        type="text"
                        value={formData.pack_fee}
                        onChange={(e) => handleInputChange('pack_fee', e.target.value)}
                        onBlur={(e) => {
                          setFormData(prev => ({
                            ...prev,
                            pack_fee: formatPriceWithDollar(formData.pack_fee)
                          }));
                        }}
                        onFocus={(e) => {
                          const raw = formData.pack_fee.toString().replace(/[^\d.]/g, '');
                          setFormData(prev => ({ ...prev, pack_fee: raw }));
                        }}
                        placeholder="0.00"
                      />
                    </div>
                    <div className="form-group">
                      <Label>{t('pricingTab.vehicleExpenses')}</Label>
                      <p style={readOnlyValueStyle}>
                        {formData.vehicle_expenses || '$0.00'}
                      </p>
                    </div>
                    <div className="form-group">
                      <Label>{t('pricingTab.floorPlanExpense')}</Label>
                      <p style={readOnlyValueStyle}>
                        {formData.floor_plan_expense || '$0.00'}
                      </p>
                    </div>
                    <div className="form-group">
                      <Label>{t('pricingTab.totalCostInCar')}</Label>
                      <p style={readOnlyValueStyle}>
                        {formData.total_cost_in_car || '$0.00'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              {/* Internet Marketing Uploads — old Buyers Guide column */}
              <div className="col-md-4 mb-4">
                <div className="card internet-marketing-uploads">
                  <div className="card-header font-weight-bold text-uppercase">
                    {t('pricingTab.internetMarketingUploads')}
                  </div>
                  <div className="card-body">
                    {MARKETING_PROVIDERS.map((provider) => (
                      <FormGroup check key={provider.key} className="mb-2">
                        <Label check className="mb-0 d-flex align-items-center">
                          <Input
                            type="checkbox"
                            className="form-check-input"
                            id={`inventory-marketing-${provider.key}`}
                            name={provider.key}
                            checked={!!formData.marketing?.[provider.key]}
                            onChange={() => handleMarketingToggle(provider.key)}
                          />
                          <span className="form-check-sign">
                            <span className="check" />
                          </span>
                          <span style={{ marginLeft: 8 }}>{t(provider.labelKey)}</span>
                        </Label>
                      </FormGroup>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      }
    </div>
  );
}
);

export default PricingTab; 